// Session PJ2: one recorded value on the office's survey panel, shown with who recorded it and edited in place. A value the office
// changes is saved as the office's own cell (the server times and stamps it) and from then on reads "Corrected by the office".
// One control per value (Edit); the editor opens under it and closes on Save or Cancel.
import { useId, useState } from "react";
import { Button } from "../../../components/Button";
import type { SurveyCell, SurveyValue } from "../../../lib/surveyRecord";
import { fromLocalInput, noAccessWords, parseTypedNumber, recordedByWords, toLocalInput, valueWords, type ValueSpec } from "./surveyOfficeUi";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50 max-sm:min-h-11";
const quietBtn = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50 max-sm:min-h-11";

const OTHER = "__other";

export function EditableValue({
  label,
  spec,
  cell,
  value,
  otherWords,
  onSave,
}: {
  label: string;
  spec: ValueSpec;
  cell: SurveyCell | undefined;
  value: SurveyValue | undefined;
  /** The words kept in the section's notes for an "Other" chip (the phone puts them there). */
  otherWords?: string;
  /** Save the value (null clears it). Resolves to a plain-English problem, or null when it saved. */
  onSave: (v: SurveyValue | null) => Promise<string | null>;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [choice, setChoice] = useState("");
  const [multi, setMulti] = useState<string[]>([]);
  const id = useId();

  const na = cell?.na;
  const shown = valueWords(spec, value, otherWords);
  const who = recordedByWords(cell);

  function open() {
    setProblem(null);
    if (spec.kind === "chips_multi") setMulti(Array.isArray(value) ? value : []);
    else if (spec.kind === "chips_single" || spec.kind === "yes_no" || spec.kind === "yes_no_unknown") {
      const v = typeof value === "string" ? value : "";
      const known = spec.options?.some((o) => o.value === v) || spec.kind !== "chips_single";
      if (v === "other") {
        setChoice(OTHER);
        setText(otherWords ?? "");
      } else if (v && !known && spec.allowOther) {
        setChoice(OTHER);
        setText(v);
      } else {
        setChoice(v);
        setText("");
      }
    } else if (spec.kind === "datetime") setText(typeof value === "string" ? toLocalInput(value) : "");
    else setText(value === undefined ? "" : Array.isArray(value) ? value.join(", ") : String(value));
    setEditing(true);
  }

  async function save(clear: boolean) {
    let v: SurveyValue | null = null;
    if (!clear) {
      switch (spec.kind) {
        case "chips_multi":
          v = multi.length ? multi : null;
          break;
        case "chips_single":
        case "yes_no":
        case "yes_no_unknown":
          if (choice === OTHER) v = text.trim() || "other";
          else v = choice || null;
          break;
        case "mm":
        case "number": {
          if (text.trim() === "") v = null;
          else {
            const n = parseTypedNumber(text);
            if (n == null) {
              setProblem("Type a number.");
              return;
            }
            v = n;
          }
          break;
        }
        case "datetime": {
          if (text.trim() === "") v = null;
          else {
            const iso = fromLocalInput(text);
            if (!iso) {
              setProblem("Choose a date and time.");
              return;
            }
            v = iso;
          }
          break;
        }
        default:
          v = text.trim() === "" ? null : text;
      }
    }
    setBusy(true);
    setProblem(null);
    const err = await onSave(v);
    setBusy(false);
    if (err) setProblem(err);
    else setEditing(false);
  }

  return (
    <div className="border-b border-ele-card-border py-2 last:border-b-0" data-value-row={label}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-ele-muted">{label}</p>
          {na ? (
            <p className="text-sm text-ele-strong">{noAccessWords(na)}</p>
          ) : shown ? (
            <p className="break-words text-sm text-ele-strong">{shown}</p>
          ) : (
            <p className="text-sm text-ele-muted">Not recorded</p>
          )}
          {who && (shown || na) ? <p className="text-xs text-ele-muted">{who}</p> : null}
        </div>
        {!editing ? (
          <button type="button" className={`${quietBtn} shrink-0`} onClick={open} aria-label={`Edit ${label}`}>
            Edit
          </button>
        ) : null}
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          {spec.kind === "chips_multi" ? (
            <fieldset className="space-y-1">
              <legend className="sr-only">{label}</legend>
              {(spec.options ?? []).map((o) => (
                <label key={o.value} className="flex min-h-9 max-sm:min-h-11 items-center gap-2 text-sm text-ele-body">
                  <input
                    type="checkbox"
                    checked={multi.includes(o.value)}
                    onChange={() => setMulti((m) => (m.includes(o.value) ? m.filter((x) => x !== o.value) : [...m, o.value]))}
                  />
                  {o.label}
                </label>
              ))}
            </fieldset>
          ) : spec.kind === "chips_single" || spec.kind === "yes_no" || spec.kind === "yes_no_unknown" ? (
            <>
              <select id={id} aria-label={label} className={inputCls} value={choice} onChange={(e) => setChoice(e.target.value)}>
                <option value="">Not recorded</option>
                {(spec.kind === "yes_no"
                  ? [
                      { value: "yes", label: "Yes" },
                      { value: "no", label: "No" },
                    ]
                  : spec.kind === "yes_no_unknown"
                    ? [
                        { value: "yes", label: "Yes" },
                        { value: "no", label: "No" },
                        { value: "unknown", label: "Not known" },
                      ]
                    : (spec.options ?? []).filter((o) => o.value !== "other")
                ).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
                {spec.allowOther || spec.options?.some((o) => o.value === "other") ? <option value={OTHER}>Other (type it)</option> : null}
              </select>
              {choice === OTHER ? (
                <input aria-label={`${label}: other`} className={inputCls} value={text} maxLength={300} placeholder="Say what it is" onChange={(e) => setText(e.target.value)} />
              ) : null}
            </>
          ) : spec.kind === "text" ? (
            <textarea id={id} aria-label={label} rows={3} className={inputCls} value={text} maxLength={4000} onChange={(e) => setText(e.target.value)} />
          ) : spec.kind === "datetime" ? (
            <input id={id} aria-label={label} type="datetime-local" className={inputCls} value={text} onChange={(e) => setText(e.target.value)} />
          ) : (
            <div className="flex items-center gap-2">
              <input
                id={id}
                aria-label={label}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className={`${inputCls} text-right`}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              {spec.kind === "mm" ? <span className="text-xs text-ele-muted">mm</span> : spec.unit ? <span className="text-xs text-ele-muted">{spec.unit}</span> : null}
            </div>
          )}
          {problem ? (
            <p role="alert" className="text-xs text-ele-danger">
              {problem}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy} onClick={() => void save(false)}>
              {busy ? "Saving..." : "Save"}
            </Button>
            {shown || na ? (
              <button type="button" className={quietBtn} disabled={busy} onClick={() => void save(true)}>
                Clear
              </button>
            ) : null}
            <button type="button" className={quietBtn} disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
